import { useCallback, useMemo } from 'react'

import {
  Text,
  Primary13Regular,
  DateRangePicker,
  Flex
} from '@sequencehq/core-components'
import { formatDateRange, formatISO } from '@sequencehq/utils/dates'
import { AggregatedCreditNote } from 'CreditNotes/types'
import { SummaryRow } from './SummaryRow'
import { SummaryRowEditButton } from './SummaryRowEditButton'
import { usePermissions } from 'lib/rbac'

type Props = {
  updateBillingPeriod: ({
    billingPeriodStart,
    billingPeriodEnd
  }: {
    billingPeriodStart: AggregatedCreditNote['billingPeriodStart']
    billingPeriodEnd: AggregatedCreditNote['billingPeriodEnd']
  }) => void
  billingPeriodStart: AggregatedCreditNote['billingPeriodStart']
  billingPeriodEnd: AggregatedCreditNote['billingPeriodEnd']
  status: AggregatedCreditNote['status']
}

export const CreditNoteBillingPeriod = ({
  updateBillingPeriod,
  billingPeriodStart,
  billingPeriodEnd,
  status
}: Props) => {
  const formattedBillingPeriod = useMemo(() => {
    if (
      typeof billingPeriodStart === 'undefined' ||
      typeof billingPeriodEnd === 'undefined'
    ) {
      return undefined
    }

    return formatDateRange({
      from: new Date(billingPeriodStart),
      to: new Date(billingPeriodEnd)
    })
  }, [billingPeriodStart, billingPeriodEnd])

  const defaultDates = useMemo(
    () =>
      billingPeriodStart && billingPeriodEnd
        ? [new Date(billingPeriodStart), new Date(billingPeriodEnd)]
        : undefined,
    [billingPeriodStart, billingPeriodEnd]
  )

  const onSubmit = useCallback(
    (selectedDates: [Date, Date]) => {
      const formattedDates =
        selectedDates.filter(Boolean).length === 2
          ? selectedDates.map(date => formatISO(date))
          : undefined

      if (!formattedDates || formattedDates?.filter(Boolean).length === 0) {
        return
      }

      updateBillingPeriod({
        billingPeriodStart: formattedDates[0],
        billingPeriodEnd: formattedDates[1]
      })
    },
    [updateBillingPeriod]
  )

  const { can } = usePermissions()

  const dateRangePickerComponent = useMemo(() => {
    if (status !== 'DRAFT') {
      return null
    }

    return (
      <DateRangePicker
        isDisabled={!can('org.creditNote:update')}
        defaultDates={defaultDates}
        onSubmit={onSubmit}
        data-testid="credit-note-billing-period-date-picker"
      >
        {can('org.creditNote:update') && (
          <SummaryRowEditButton
            aria-label="Edit billing period"
            data-testid="credit-note-billing-period-edit"
          />
        )}
      </DateRangePicker>
    )
  }, [status, defaultDates, onSubmit, can])

  return (
    <SummaryRow
      label="Billing period"
      value={
        <Flex alignItems="center" gap={1} role="group">
          <Text
            {...Primary13Regular}
            data-testid="credit-note-billing-period-formatted"
          >
            {formattedBillingPeriod ? formattedBillingPeriod : '-'}
          </Text>
          {dateRangePickerComponent}
        </Flex>
      }
    />
  )
}
